import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { FiMenu, FiTruck, FiAlertCircle } from 'react-icons/fi';
import { Sidebar, LoadingOverlay, GlobalSearch, Toast, NotificationBell, InstallButton, GeoFenceAlert } from '../../components';
import { useThemeStore, useAuthStore, useSettingsStore, useShopStore, useNotificationStore, useToastStore } from '../../store';
import useGeoFence from '../../utils/useGeoFence';
import './Layout.css';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { theme } = useThemeStore();
  const { isAuthenticated, user, token } = useAuthStore();
  const { settings, loadSettings } = useSettingsStore();
  const { selectedShop } = useShopStore();
  const { initSocket, reportActivity } = useNotificationStore();
  const { success: toastSuccess } = useToastStore();

  // Socket global: lo abren TODOS los roles autenticados.
  // Los admins lo usan para notificaciones; todos aparecen en el panel
  // de usuarios en linea del superadmin.
  const socketShopId = user?.role === 'superadmin' ? selectedShop?._id : user?.shop;
  useEffect(() => {
    if (isAuthenticated && token) {
      initSocket(token, user?.role, socketShopId, () => {
        toastSuccess('Nueva cita recibida desde el portal');
      });
    }
  }, [isAuthenticated, token, user?.role, socketShopId]);

  // Informa la pantalla actual para el panel de usuarios en linea
  useEffect(() => {
    if (isAuthenticated) {
      reportActivity(location.pathname);
    }
  }, [isAuthenticated, location.pathname]);

  // Geocerca: una verificacion por carga de pagina. Con el modo apagado
  // (el default de fabrica es 'audit', que no molesta al usuario) no hace nada visible.
  const { verdict: geoVerdict, showAlert: showGeoAlert, isBlocked: geoBlocked, acknowledge: acknowledgeGeo, retry: retryGeo } = useGeoFence();

  // Load settings when authenticated (and reload when shop changes for superadmin)
  const shopId = selectedShop?._id;
  useEffect(() => {
    if (isAuthenticated) {
      loadSettings(true);
    }
  }, [isAuthenticated, shopId]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  // Payment reminder: show 3 days before due date (not for superadmin)
  const getPaymentReminder = () => {
    if (!settings?.paymentDueDay || user?.role === 'superadmin') return null;
    const now = new Date();
    const day = settings.paymentDueDay;
    let dueDate = new Date(now.getFullYear(), now.getMonth(), day);
    if (now.getDate() > day) {
      dueDate = new Date(now.getFullYear(), now.getMonth() + 1, day);
    }
    const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    if (diffDays >= 0 && diffDays <= 3) return { date: dueDate, urgent: diffDays <= 1 };
    return null;
  };

  const paymentReminder = getPaymentReminder();

  return (
    <div className={`wrapper-layout ${!isAuthenticated ? 'no-sidebar-layout' : ''}`}>
      {/* Mobile Header - solo si está autenticado */}
      {isAuthenticated && (
        <header className="mobile-header-layout">
          <button
            className="menu-btn-layout"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú"
          >
            <FiMenu />
          </button>
          <div className="mobile-logo-layout">
            {settings?.logo ? (
              <img src={settings.logo} alt={settings?.name || 'Logo'} className="mobile-logo-image-layout" />
            ) : (
              <div className="mobile-logo-icon-layout">
                <FiTruck />
              </div>
            )}
            <span className="mobile-logo-text-layout">{settings?.name || 'Sistema de Taller'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <InstallButton />
            <NotificationBell />
          </div>
        </header>
      )}

      {/* Overlay for mobile sidebar - solo si está autenticado */}
      {isAuthenticated && (
        <div
          className={`sidebar-overlay-layout ${sidebarOpen ? 'active-layout' : ''}`}
          onClick={handleCloseSidebar}
        />
      )}

      <Sidebar isOpen={sidebarOpen} onClose={handleCloseSidebar} collapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />

      <main className={`main-layout ${!isAuthenticated ? 'full-width-layout' : ''} ${sidebarCollapsed ? 'sidebar-collapsed-layout' : ''}`}>
        {/* Top Bar with Search and Notifications - solo si está autenticado */}
        {isAuthenticated && (
          <div className="topbar-layout">
            {paymentReminder && (
              <div className={`payment-reminder-layout ${paymentReminder.urgent ? 'urgent-layout' : ''}`}>
                <FiAlertCircle className="payment-reminder-icon-layout" />
                <span className="payment-reminder-text-layout">
                  Pago del sistema: {paymentReminder.date.toLocaleDateString('es-SV', { day: 'numeric', month: 'long' })}
                </span>
              </div>
            )}
            <GlobalSearch />
            <InstallButton />
            <NotificationBell />
          </div>
        )}
        <div className="content-layout">
          <Outlet />
        </div>
      </main>
      <LoadingOverlay />
      <Toast />
      {showGeoAlert && (
        <GeoFenceAlert
          verdict={geoVerdict}
          blocking={geoBlocked}
          onContinue={acknowledgeGeo}
          onRetry={retryGeo}
        />
      )}
    </div>
  );
}

export default Layout;
